import { BoxV2 as Box, Button, Flex, TextInput } from 'portal-commons';
import { FunctionComponent, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import Option from './Option';
import { useControllers } from './hooks';
import { AcceptCampaignFormValues } from './types';
import { campaignController } from '@/apis/core';
import { Modal } from '@/components';
import { SelectItem } from '@/types';
import { toastFlashMessage } from '@/utils';

interface AcceptCampaignModalProps {
  open?: boolean;
  primaryDca?: boolean;
  campaignUid: string;
  options?: SelectItem[];
  onCancel?: () => void;
  onSubmit?: () => void;
}

const Container: FunctionComponent = ({ children }) => (
  <Flex sx={{ flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
    {children}
  </Flex>
);

const Title: FunctionComponent = ({ children }) => (
  <Box
    sx={{
      padding: '12px 0',
      color: 't.black80',
      fontWeight: 600,
      fontSize: 'H600',
      lineHeight: 'normal',
    }}
  >
    {children}
  </Box>
);

const Message: FunctionComponent = ({ children }) => (
  <Box
    sx={{
      width: '100%',
      color: 't.black80',
      fontWeight: 400,
      fontSize: 'H300',
      lineHeight: 'normal',
    }}
  >
    {children}
  </Box>
);

const Options: FunctionComponent = ({ children }) => (
  <Flex
    sx={{
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '6px',
      marginTop: 'm',
    }}
  >
    {children}
  </Flex>
);

const SectionTitle: FunctionComponent = ({ children }) => (
  <Box
    sx={{
      color: 't.black70',
      fontWeight: 600,
      fontSize: 'H400',
      lineHeight: 'normal',
    }}
  >
    {children}
  </Box>
);

const Actions: FunctionComponent = ({ children }) => (
  <Flex
    sx={{
      flexDirection: 'row',
      alignItems: 'center',
      gap: 'l',
      padding: '30px 0 40px',
    }}
  >
    {children}
  </Flex>
);

const AcceptCampaignModal: FunctionComponent<AcceptCampaignModalProps> = ({
  open = false,
  primaryDca = false,
  campaignUid,
  options = [],
  onCancel,
  onSubmit,
}) => {
  const {
    formState: { errors },
    control,
    reset,
    handleSubmit,
  } = useForm<AcceptCampaignFormValues>({
    defaultValues: {
      excludedMnoIds: [],
      explanation: '',
    },
  });
  const {
    excludedMnoIds,
    setExcludedMnoIds,
    explanation,
    setExplanation,
    validateExplanation,
    explanationError,
  } = useControllers(control);
  const [proceeding, setProceeding] = useState(false);

  const handleValid = async () => {
    setProceeding(true);
    const response = await (primaryDca
      ? campaignController.acceptCampaignAsPrimaryDca(campaignUid, {
          excludedMnoIds,
          explanation,
        })
      : campaignController.acceptCampaignAsSecondaryDca(campaignUid, {
          excludedMnoIds,
          explanation,
        }));
    if (response) {
      if (excludedMnoIds.length > 0) {
        options.forEach((option) => {
          if (excludedMnoIds.includes(option.value)) {
            toastFlashMessage(
              `${option.label} successfully excluded`,
              'success'
            );
          }
        });
      }
      onSubmit?.();
    }
    setProceeding(false);
  };

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open]);

  useEffect(() => {
    if (excludedMnoIds.length < 1) {
      setExplanation('');
    }
  }, [excludedMnoIds]);

  return (
    <Modal style={{ width: '560px' }} open={open}>
      <form noValidate onSubmit={handleSubmit(handleValid)}>
        <Container>
          <Title>Accept Campaign</Title>
          <Box>
            <Message>
              {primaryDca
                ? 'You are accepting election as the primary DCA for this campaign. You will now provision it for the MNOs you are directly connected to and elect secondary DCAs for any remaining MNOs.'
                : 'You are accepting election as the secondary DCA for this campaign.'}
              <br />
              <br />
              <span style={{ fontWeight: 700 }}>MNO Exclusion</span>
              <br />
              If you would like to exclude any connected MNOs from this
              campaign, select them below. You cannot exclude an MNO following
              campaign acceptance.
            </Message>
            <Options>
              <SectionTitle>Connected MNOs</SectionTitle>
              {options.map((option) => (
                <Option
                  key={option.value}
                  checked={excludedMnoIds.includes(option.value)}
                  onChange={(checked) => {
                    if (checked) {
                      setExcludedMnoIds([...excludedMnoIds, option.value]);
                    } else {
                      const newExcludedMnoIds = excludedMnoIds.filter(
                        (excludedMnoId) => excludedMnoId !== option.value
                      );
                      setExcludedMnoIds(newExcludedMnoIds);
                    }
                  }}
                >
                  {option.label}
                </Option>
              ))}
            </Options>
            {excludedMnoIds.length > 0 && (
              <TextInput
                style={{ marginTop: '25px' }}
                multiline
                error={!!explanationError?.message}
                minRows={3}
                maxRows={3}
                maxLength={2048}
                label="Explanation (Required)"
                note={explanationError?.message}
                value={explanation}
                onChange={setExplanation}
                onBlur={validateExplanation}
              />
            )}
          </Box>
          <Actions>
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                proceeding ||
                Object.values(errors).some((error) => !!error.message)
              }
            >
              Confirm
            </Button>
          </Actions>
        </Container>
      </form>
    </Modal>
  );
};

export default AcceptCampaignModal;
